import React, { useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useReportTabs } from '@/contexts/ReportTabsContext';
import { useWorkspaceTitle, useWorkspace } from '@/contexts/WorkspaceContext';
import ReportBuilderPage from '@/pages/ReportBuilderPage';
import { TooltipProvider } from '@/components/ui/tooltip';
import { getTerm } from '@/lib/branding';

const mkDraftToken = () => Math.random().toString(36).slice(2, 9) + Date.now().toString(36).slice(-4);

export default function ReportTabsPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const reportId = searchParams.get('id');
  const newFlag = searchParams.get('new');
  const draftParam = searchParams.get('draft');
  const { tabs, activeTabId, openTab, openDraft, closeTab, updateTab } = useReportTabs();
  const ws = useWorkspace();

  // Label the Workbench dock chip with the active insight's real name
  // (falls back to the resolver default while a tab is still loading/new).
  const activeTab = tabs.find(t => t.id === activeTabId);
  const _newLabel = `New ${getTerm('insights', 'singular')}`;
  const activeLabel = activeTab && activeTab.label
    && !['New Report', 'Loading...', _newLabel].includes(activeTab.label) ? activeTab.label : undefined;
  useWorkspaceTitle(activeLabel);

  // Resolve which tab to show from the URL.
  //  • ?id=<rid>  → open/activate that saved insight
  //  • ?new=1     → explicitly start a fresh insight tab (from "+ New" / switcher)
  //  • otherwise  → returning to the builder: keep existing tabs (restores the
  //                 in-progress draft) and only create a blank tab if none exist.
  const initialTabCreated = React.useRef(false);
  useEffect(() => {
    if (reportId) {
      openTab(reportId);
      return;
    }
    if (draftParam) {
      openDraft(draftParam);
      return;
    }
    if (newFlag === '1') {
      // Mint a unique draft token so every "New" is its own report + chip, and
      // reflect it in the URL (survives refresh, distinct Workbench session).
      const p = new URLSearchParams(searchParams);
      p.delete('new');
      p.set('draft', mkDraftToken());
      setSearchParams(p, { replace: true });
      return;
    }
    if (tabs.length === 0 && !initialTabCreated.current) {
      initialTabCreated.current = true;
      const p = new URLSearchParams(searchParams);
      p.set('draft', mkDraftToken()); // first visit → one fresh, uniquely-keyed tab
      setSearchParams(p, { replace: true });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportId, newFlag, draftParam]);

  const handleCloseTab = (tabId) => {
    const tab = tabs.find(t => t.id === tabId);
    const remaining = tabs.filter(t => t.id !== tabId);
    // Retire the matching Workbench session chip so the report is fully gone.
    let wsKey = null;
    if (tab?.reportId) wsKey = `insight:${tab.reportId}`;
    else if (tabId.startsWith('draft:')) wsKey = `insight:draft:${tabId.slice(6)}`;
    else wsKey = 'insight:new';
    try { ws?.closeTab(wsKey); } catch (_) { /* non-fatal */ }
    closeTab(tabId);
    // Point the URL at whatever tab is now active (or leave the builder if none)
    // so the location→session effect can't re-open the tab we just closed.
    const nextTab = remaining[remaining.length - 1];
    if (nextTab) {
      const path = nextTab.reportId
        ? `/dashboards/builder?id=${nextTab.reportId}`
        : nextTab.id.startsWith('draft:')
          ? `/dashboards/builder?draft=${nextTab.id.slice(6)}`
          : '/dashboards/builder';
      navigate(path, { replace: true });
    } else {
      navigate('/dashboards');
    }
  };

  // Navigate to reports list when user explicitly closes all tabs
  const hadTabs = React.useRef(false);
  useEffect(() => {
    if (tabs.length > 0) hadTabs.current = true;
    if (tabs.length === 0 && hadTabs.current) {
      navigate('/dashboards');
    }
  }, [tabs.length, navigate]);

  if (tabs.length === 0) return null;

  return (
    <TooltipProvider>
    <div className="h-full flex flex-col" data-testid="report-tabs-page">
      {/* Top tab bar removed — the bottom Workbench dock is the single place
          that shows and switches open sessions. */}

      {/* Tab content — each tab is a separate ReportBuilderPage */}
      <div className="flex-1 overflow-hidden relative">
        {tabs.map(tab => (
          <div key={tab.id} className={`absolute inset-0 ${activeTabId === tab.id ? 'z-10 visible' : 'pointer-events-none'}`} style={activeTabId !== tab.id ? { visibility: 'hidden', display: 'none' } : undefined}>
            <ReportBuilderPage
              tabId={tab.id}
              initialReportId={tab.reportId}
              reloadNonce={tab.reloadNonce}
              onLabelChange={(label) => updateTab(tab.id, { label })}
              onReportIdChange={(rid) => updateTab(tab.id, { reportId: rid })}
              onClose={() => handleCloseTab(tab.id)}
              onOpenReport={(rid) => openTab(rid)}
              isTabbed
            />
          </div>
        ))}
      </div>
    </div>
    </TooltipProvider>
  );
}
